import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { STORY_TYPES } from '../types/story';
import Logo from '../components/Logo';
import { api } from '../services/api';

interface GenerationProgress {
  stage: 'text_generation' | 'audio_generation' | 'uploading';
  progress: number;
  message: string;
}

interface Story {
  story_id: string;
  title: string;
  story_type: string;
  word_count: number;
  status: string;
  created_at: string;
  has_audiobook: boolean;
  progress?: GenerationProgress | null;
}

export default function Stories() {
  const navigate = useNavigate();
  const { user, clearAuth } = useAuthStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [stories, setStories] = useState<Story[]>([]);

  // Fetch stories from API
  useEffect(() => {
    const fetchStories = async () => {
      try {
        const data = await api.getStories();
        setStories(data as unknown as Story[]);
      } catch (error) {
        console.error('Error fetching stories:', error);
      }
    };

    fetchStories();

    // Poll every 3 seconds if there are generating stories
    const pollInterval = setInterval(() => {
      const hasGeneratingStories = stories.some(s => s.status === 'generating');
      if (hasGeneratingStories) {
        fetchStories();
      }
    }, 3000);

    return () => clearInterval(pollInterval);
  }, [stories]);

  const handleLogout = () => {
    clearAuth();
    navigate('/');
  };

  const filteredStories = stories.filter((story) => {
    const matchesSearch = story.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFilter = filterType === 'all' || story.story_type === filterType;
    return matchesSearch && matchesFilter;
  });

  const getStatusBadge = (status: string, progress?: GenerationProgress | null) => {
    switch (status) {
      case 'completed':
        return (
          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
            ✓ Ready
          </span>
        );
      case 'generating':
        const stageEmoji = progress?.stage === 'text_generation' ? '📝' :
                          progress?.stage === 'audio_generation' ? '🎙️' :
                          progress?.stage === 'uploading' ? '☁️' : '⏳';
        const stageText = progress?.stage === 'text_generation' ? 'Writing Story' :
                         progress?.stage === 'audio_generation' ? 'Creating Audio' :
                         progress?.stage === 'uploading' ? 'Uploading' : 'Starting';
        return (
          <div className="flex flex-col gap-1">
            <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold flex items-center gap-1">
              <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {stageEmoji} {stageText}
            </span>
            {progress && progress.stage === 'audio_generation' && progress.progress > 0 && (
              <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                <div
                  className="bg-yellow-500 h-full transition-all duration-300"
                  style={{ width: `${progress.progress}%` }}
                ></div>
              </div>
            )}
          </div>
        );
      case 'failed':
        return (
          <span className="px-3 py-1 bg-red-100 text-red-700 rounded-full text-xs font-semibold">
            ✗ Failed
          </span>
        );
      default:
        return (
          <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">
            {status}
          </span>
        );
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));

    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours} hours ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen bg-background-cream">
      {/* Header */}
      <header className="bg-white shadow-soft">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Logo size="md" />
            <button
              onClick={() => navigate('/dashboard')}
              className="text-primary-600 hover:text-primary-700 font-semibold"
            >
              Dashboard
            </button>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-sm text-primary-500">Logged in as</p>
              <p className="text-emerald-600 font-semibold">{user?.email}</p>
            </div>
            <button
              onClick={handleLogout}
              className="btn-outline px-4 py-2 text-sm"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Actions Bar */}
        <div className="flex flex-col md:flex-row gap-4 mb-8">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search stories..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input-field"
            />
          </div>

          {/* Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input-field md:w-64"
          >
            <option value="all">All Story Types</option>
            {STORY_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.emoji} {type.label}
              </option>
            ))}
          </select>

          {/* Create New Button */}
          <button
            onClick={() => navigate('/create-story')}
            className="btn-primary whitespace-nowrap"
          >
            + Create New Story
          </button>
        </div>

        {/* Stories Table */}
        {filteredStories.length > 0 ? (
          <div className="bg-white rounded-2xl shadow-soft overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Story</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Type</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Length</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Created</th>
                    <th className="px-6 py-4 text-left text-sm font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredStories.map((story) => {
                    const storyType = STORY_TYPES.find((t) => t.value === story.story_type);

                    return (
                      <tr
                        key={story.story_id}
                        className="hover:bg-primary-50/50 transition-colors cursor-pointer"
                        onClick={() => navigate(`/stories/${story.story_id}`)}
                      >
                        {/* Story Title */}
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <span className="text-3xl">{storyType?.emoji}</span>
                            <div>
                              <h3 className="font-display font-bold text-primary-600 text-lg">
                                {story.title}
                              </h3>
                            </div>
                          </div>
                        </td>

                        {/* Story Type */}
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-700 font-semibold">
                            {storyType?.label}
                          </span>
                        </td>

                        {/* Word Count */}
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-600">
                            {story.word_count.toLocaleString()} words
                          </span>
                        </td>

                        {/* Status */}
                        <td className="px-6 py-4">
                          {getStatusBadge(story.status, story.progress)}
                        </td>

                        {/* Created Date */}
                        <td className="px-6 py-4">
                          <span className="text-sm text-gray-500">
                            {formatDate(story.created_at)}
                          </span>
                        </td>

                        {/* Actions */}
                        <td className="px-6 py-4">
                          <div onClick={(e) => e.stopPropagation()}>
                            {story.status === 'completed' && (
                              <button
                                onClick={() => navigate(`/stories/${story.story_id}`)}
                                className="btn-primary px-4 py-2 text-sm"
                              >
                                Listen 🎧
                              </button>
                            )}
                            {story.status === 'generating' && (
                              <button className="btn-outline px-4 py-2 text-sm" disabled>
                                Generating...
                              </button>
                            )}
                            {story.status === 'failed' && (
                              <button className="bg-red-500 hover:bg-red-600 text-white font-semibold px-4 py-2 rounded-xl text-sm">
                                Retry
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          /* Empty State */
          <div className="text-center py-16">
            <div className="text-8xl mb-4">📚</div>
            <h3 className="text-2xl font-display font-bold text-gray-900 mb-2">
              {searchQuery || filterType !== 'all'
                ? 'No stories found'
                : 'No stories yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchQuery || filterType !== 'all'
                ? 'Try adjusting your search or filter'
                : 'Create your first personalized audiobook'}
            </p>
            <button
              onClick={() => navigate('/create-story')}
              className="btn-primary"
            >
              Create Your First Story ✨
            </button>
          </div>
        )}
      </main>
    </div>
  );
}
